import React from 'react';
import { LegalShell, Section, List } from '../../components/legal/LegalShell';

const RefundPolicy = () => (
  <LegalShell eyebrow="Legal" title="Refund & Cancellation Policy" updated="07 August 2026">
    <p>This Refund &amp; Cancellation Policy explains when you may cancel your maintenance service plan or other services provided by Domestic Home Services Limited and when you may be entitled to a refund.</p>
    <p>This policy should be read together with our Terms &amp; Conditions, Welcome Letter, Plan Certificate and Invoice where applicable.</p>
    <p>Nothing in this policy affects your statutory consumer rights.</p>

    <Section n="1" title="14-Day Cooling-Off Period">
      <p>If you purchase a maintenance service plan by telephone, online or through another qualifying distance-selling method, you will normally have a 14-day cooling-off period beginning from the date the contract is entered into.</p>
      <p>You may cancel during this period without giving us a reason.</p>
      <p>Where no repair, engineer attendance, maintenance service or other service has been requested or provided during the cooling-off period, any plan payment received will normally be refunded in full.</p>
    </Section>

    <Section n="2" title="Services Requested During the Cooling-Off Period">
      <p>You may ask us to begin providing services before the 14-day cooling-off period has ended.</p>
      <p>For example, you may ask us to arrange an engineer or begin providing another eligible service.</p>
      <p>Where you have expressly requested that services begin during the cooling-off period and subsequently cancel before the period ends, we may deduct a reasonable and proportionate amount for services already provided where permitted by applicable consumer law.</p>
      <p>If the service has been fully performed during the cooling-off period following your express request and acknowledgement, your statutory cancellation rights may be affected as permitted by law.</p>
    </Section>

    <Section n="3" title="Cancellation After the Cooling-Off Period">
      <p>You may request cancellation of your maintenance service plan after the cooling-off period by contacting our Customer Care Team.</p>
      <p>Any refund after the cooling-off period will depend upon:</p>
      <List items={[
        'The type of plan purchased.',
        'The amount you have paid.',
        'How long the plan has been active.',
        'Services already provided.',
        'Engineer visits already arranged or completed.',
        'Repair costs incurred.',
        'Parts supplied or ordered specifically for your repair.',
        'Any replacement or financial contribution already provided.',
        'Any applicable administration charge disclosed within your agreement.',
      ]} />
      <p>Where a refund is due, we will provide you with an explanation of how the amount has been calculated.</p>
    </Section>

    <Section n="4" title="Administration Charges">
      <p>Where permitted under your agreement and applicable law, an administration charge may apply when a customer cancels after the cooling-off period.</p>
      <p>Where applicable, the charge will normally be between £45 and £70, depending upon the maintenance plan and the administrative work involved.</p>
      <p>Any applicable charge will be communicated clearly and will not affect rights that cannot legally be excluded or restricted.</p>
    </Section>

    <Section n="5" title="Repairs or Services Already Provided">
      <p>Where we have already incurred reasonable costs in providing services under your plan, those costs may be taken into consideration when calculating any refund due after cancellation.</p>
      <p>This may include:</p>
      <List items={[
        'Engineer attendance.',
        'Diagnostic work.',
        'Repair work.',
        'Replacement parts.',
        'Contractor costs.',
        'Replacement contributions.',
        'Other services authorised under your maintenance plan.',
      ]} />
      <p>We will not make deductions that we are not legally or contractually entitled to make.</p>
    </Section>

    <Section n="6" title="Replacement Appliances or Contributions">
      <p>Where an appliance has already been replaced, or a replacement contribution or other significant benefit has been provided under the plan, this may affect whether any further refund is payable.</p>
      <p>The circumstances of each cancellation will be reviewed individually and in accordance with the Terms &amp; Conditions of the customer's maintenance service plan.</p>
    </Section>

    <Section n="7" title="Ad-Hoc Repair Bookings">
      <p>Where Domestic Home Services Limited offers a one-off repair service outside a maintenance service plan, separate cancellation and refund arrangements may apply.</p>
      <p>If we cancel a confirmed repair appointment and are unable to provide an appropriate alternative, any amount paid specifically for the cancelled service may be refunded where appropriate.</p>
      <p>Where an engineer has already attended, carried out diagnostic work or provided part of the agreed service, reasonable charges for work already performed may still apply.</p>
      <p>An unsuccessful repair does not automatically mean that the entire service charge will be refundable where diagnostic work, attendance or other services have legitimately been provided.</p>
      <p>Any refund entitlement will be assessed according to the circumstances and applicable consumer rights.</p>
    </Section>

    <Section n="8" title="When a Refund May Not Be Payable">
      <p>A full or partial refund may not be payable where, for example:</p>
      <List items={[
        'Services have already been provided.',
        'An engineer has already attended.',
        'Parts have already been supplied or specifically ordered.',
        'A repair has been completed.',
        'A replacement appliance or contribution has already been provided.',
        'The plan has been suspended or cancelled due to non-payment.',
        'The customer has provided materially false or misleading information.',
        'There has been fraud, attempted fraud or misuse of the service.',
        'The amount properly due for services already provided equals or exceeds the refundable balance.',
      ]} />
      <p>Each case will be considered in accordance with these Terms and applicable consumer law.</p>
    </Section>

    <Section n="9" title="Faulty or Unsatisfactory Services">
      <p>Your statutory consumer rights are separate from our general cancellation policy.</p>
      <p>If you believe a service has not been provided with reasonable care and skill or otherwise does not meet applicable legal requirements, please contact us.</p>
      <p>We will review the circumstances and, where appropriate, may:</p>
      <List items={[
        'Arrange for the service to be repeated.',
        'Arrange corrective work.',
        'Offer an appropriate price reduction.',
        'Provide a partial or full refund where legally required.',
        'Offer another reasonable resolution.',
      ]} />
      <p>Nothing in this Refund Policy limits your statutory rights under applicable UK consumer law.</p>
    </Section>

    <Section n="10" title="How Refunds Are Paid">
      <p>Where a refund has been approved, we will normally return the funds using the same payment method used for the original transaction where reasonably possible.</p>
      <p>For payments originally made by bank transfer, refunds will generally be returned by bank transfer.</p>
      <p>For security and fraud-prevention purposes, we may request verification of the receiving bank account before processing a refund.</p>
      <p>We will never ask you to provide your online banking password, PIN or full security credentials.</p>
    </Section>

    <Section n="11" title="Refund Processing Times">
      <p>Where you exercise a statutory right to cancel during the applicable cooling-off period, refunds will be processed within the timeframe required by applicable consumer law.</p>
      <p>For other approved refunds, we aim to process the refund as soon as reasonably possible after the amount has been agreed.</p>
      <p>Bank processing times may vary and are outside our control once the refund has been sent.</p>
    </Section>

    <Section n="12" title="How to Cancel or Request a Refund">
      <p>To cancel your maintenance service plan or request a refund, please contact:</p>
      <p>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>Please provide where possible:</p>
      <List items={[
        'Your full name.',
        'Your Plan Number.',
        'Your address or postcode.',
        'The reason for contacting us.',
        'Any relevant repair or service reference.',
      ]} />
      <p>You do not have to provide a reason when exercising a statutory right to cancel during an applicable cooling-off period.</p>
      <p>We will confirm the outcome of your cancellation request and any refund due.</p>
    </Section>

    <Section n="13" title="Complaints About a Refund">
      <p>If you disagree with a cancellation decision or refund calculation, please contact our Customer Care Team.</p>
      <p>We will review your concerns fairly and provide an explanation of our decision.</p>
      <p>If appropriate, your complaint may be escalated internally for further review.</p>
      <p>Information about our complaints process is available within our Compliance Policy and Terms &amp; Conditions.</p>
      <p>Nothing prevents you from obtaining independent consumer advice or exercising any statutory legal rights available to you.</p>
    </Section>

    <Section n="14" title="Fraud & Incorrect Payments">
      <p>Where we identify a suspected fraudulent transaction, duplicate payment or payment made in error, we may carry out reasonable verification before issuing a refund.</p>
      <p>This is intended to protect customers and Domestic Home Services Limited against fraud and unauthorised transactions.</p>
    </Section>

    <Section n="15" title="Changes to This Policy">
      <p>We may update this Refund &amp; Cancellation Policy periodically to reflect:</p>
      <List items={[
        'Changes to our services.',
        'Changes to our business procedures.',
        'Changes in applicable consumer law.',
        'Improvements to our customer service processes.',
      ]} />
      <p>The latest version will be made available through our website.</p>
      <p>Material changes affecting an existing customer's contractual rights will be handled in accordance with applicable law and the customer's agreement.</p>
    </Section>

    <Section n="16" title="Contact Us">
      <p>For cancellation, refund or general customer service enquiries, please contact:</p>
      <p><b>Domestic Home Services Limited</b><br/>Registered Office:<br/>128 City Road<br/>London<br/>United Kingdom<br/>EC1V 2NX</p>
      <p>Company Number: 17368184<br/>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>Domestic Home Services Limited is committed to handling cancellations and refunds fairly, transparently and in accordance with applicable UK consumer law.</p>
    </Section>
  </LegalShell>
);

export default RefundPolicy;
